import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FileCheck } from "lucide-react";
import { API } from "../../config";
import { useAuth } from "../../context/AuthContext";

/**
 * Header quick-access pill to the Legal Documents CMS.
 *
 * Visibility rules (mirror the LegalDocumentsWidget):
 *  - super_admin & admin → render the button
 *  - it / viewer / salesforce → hidden (no edit permissions anyway)
 *
 * Shows a small red counter badge with the number of drafts pending
 * activation so the user has an at-a-glance signal that there's work
 * to review without opening the dedicated page.
 */
export const LegalQuickAccessButton = () => {
  const [drafts, setDrafts] = useState(0);
  const [stale, setStale] = useState(0);
  const navigate = useNavigate();
  const { getAuthHeader, user } = useAuth();

  const canSee = user?.role === "super_admin" || user?.role === "admin";

  useEffect(() => {
    if (!canSee) return;
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/legal-documents/_stats/summary`, getAuthHeader());
        if (!alive) return;
        setDrafts(r.data?.total_drafts || 0);
        setStale((r.data?.stale_drafts || []).length);
      } catch { /* silently hide badge on error */ }
    })();
    return () => { alive = false; };
  }, [canSee]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (!canSee) return null;

  return (
    <button
      onClick={() => navigate("/admin/legal")}
      className="btn-lojack-outline flex items-center gap-2 text-sm relative"
      data-testid="header-legal-btn"
      title={drafts > 0 ? `${drafts} bozza/e in attesa di attivazione${stale > 0 ? ` · ${stale} da oltre 7 giorni` : ""}` : "Gestisci documenti legali"}
    >
      <FileCheck size={16} />
      Doc. Legali
      {drafts > 0 && (
        <span
          className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold tabular-nums leading-none ${stale > 0 ? 'bg-red-500 text-white animate-pulse' : 'bg-lojack-clause-500 text-black'}`}
          data-testid="header-legal-badge"
        >
          {drafts}
        </span>
      )}
    </button>
  );
};
